import Button from "@components/blocks/Button";
import CalculationIcon from "@components/Calculation/CalculationIcon";
import NumberInput from "@components/hookform/NumberInput";
import ControlledSelect from "@components/hookform/Select";
import ControlledSwitch from "@components/hookform/Switch";
import Section from "@components/Section";
import SimpleRichText from "@components/SimpleRichText";
import { formatMoney } from "@justhome/common";
import { useGetAssumptions } from "@justhome/api-core";
import { Title } from "@justhome/react-ui";
import { ComponentSectionsPurchaseCostCalculator } from "@lib/generated/graphql";
import { getFederalStateOptions } from "@lib/labels";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { useTranslation } from "next-i18next";
import { FC, useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { useForm } from "react-hook-form";

ChartJS.register(ArcElement, Tooltip, Legend);

interface PropertyPurchaseCostsCalculatorProps {
  data: ComponentSectionsPurchaseCostCalculator;
}

interface PropertyPurchaseCostsForm {
  purchasePrice: number;
  federalState: string;
  propertyTransferTax: number | undefined;
  propertyTransferTaxPercentage: number | undefined;
  notaryAndLandRegistryCosts: number | undefined;
  notaryAndLandRegistryCostsPercentage: number | undefined;
  paysRealEstateCommission: boolean;
  realEstateCommission: number | undefined;
  realEstateCommissionPercentage: number | undefined;
}

interface CalculationResult {
  purchasePrice: number;
  propertyTransferTax: number;
  notaryAndLandRegistryCosts: number;
  realEstateCommission: number;
  totalCosts: number;
}

interface ResultRowProps {
  label: string;
  value: number;
  isTotal?: boolean;
}

const ResultRow: FC<ResultRowProps> = ({ label, value, isTotal = false }) => {
  const className = isTotal ? "text-h4" : "";
  const labelClassName = isTotal ? "" : "text-neutral-675/60";
  const valueClassName = isTotal ? "font-book" : "";

  return (
    <div className={`flex justify-between ${className}`}>
      <span className={labelClassName}>{label}</span>
      <span className={valueClassName}>{formatMoney(value)}</span>
    </div>
  );
};

const federalStateOptions = getFederalStateOptions();

const PropertyPurchaseCostsCalculator: FC<
  PropertyPurchaseCostsCalculatorProps
> = ({ data }) => {
  const { t } = useTranslation("property-costs-calculator");

  const { control, watch, setValue } = useForm<PropertyPurchaseCostsForm>({
    defaultValues: {
      purchasePrice: 150000,
      federalState: "BERLIN",
      propertyTransferTax: undefined,
      propertyTransferTaxPercentage: undefined,
      notaryAndLandRegistryCosts: undefined,
      notaryAndLandRegistryCostsPercentage: undefined,
      paysRealEstateCommission: true,
      realEstateCommission: undefined,
      realEstateCommissionPercentage: undefined,
    },
    mode: "onChange",
  });

  const [result, setResult] = useState<CalculationResult | null>(null);

  const federalState = watch("federalState");
  const purchasePrice = watch("purchasePrice");
  const paysRealEstateCommission = watch("paysRealEstateCommission");

  const costInputsEnabled = !!(purchasePrice && federalState);

  const { data: assumptions } = useGetAssumptions(
    {
      queryParams: {
        federalState: federalState,
      },
    },
    {
      enabled: !!federalState,
    },
  );

  useEffect(() => {
    if (!assumptions || !purchasePrice) {
      return;
    }

    if (assumptions.propertyTransferTaxPercentage) {
      const propertyTransferTax = Math.round(
        (purchasePrice * assumptions.propertyTransferTaxPercentage) / 100,
      );
      setValue("propertyTransferTax", propertyTransferTax);
      setValue(
        "propertyTransferTaxPercentage",
        assumptions.propertyTransferTaxPercentage,
      );
    }

    if (assumptions.notaryFeePercentage) {
      const notaryAndLandRegistryCosts = Math.round(
        (purchasePrice * assumptions.notaryFeePercentage) / 100,
      );
      setValue("notaryAndLandRegistryCosts", notaryAndLandRegistryCosts);
      setValue(
        "notaryAndLandRegistryCostsPercentage",
        assumptions.notaryFeePercentage,
      );
    }

    if (assumptions.realEstateAgentFeePercentage) {
      const realEstateCommission = Math.round(
        (purchasePrice * assumptions.realEstateAgentFeePercentage) / 100,
      );
      setValue("realEstateCommission", realEstateCommission);
      setValue(
        "realEstateCommissionPercentage",
        assumptions.realEstateAgentFeePercentage,
      );
    }
  }, [assumptions, purchasePrice, setValue]);

  useEffect(() => {
    const watcher = watch((values, change) => {
      if (change.type !== "change") {
        return;
      }

      // Skip if the change was triggered by setValue to prevent infinite loops
      if (change.name?.includes("Percentage")) {
        const baseField = change.name.replace("Percentage", "") as
          | "propertyTransferTax"
          | "notaryAndLandRegistryCosts"
          | "realEstateCommission";
        const percentageValue = values[
          change.name as keyof PropertyPurchaseCostsForm
        ] as number;
        const euroValue = values[baseField] as number;

        if (
          typeof percentageValue === "number" &&
          typeof values.purchasePrice === "number"
        ) {
          const calculatedEuroAmount = Math.round(
            (values.purchasePrice * percentageValue) / 100,
          );

          // Only update if the calculated value is different from current value
          if (calculatedEuroAmount !== euroValue) {
            setValue(baseField, calculatedEuroAmount, {
              shouldValidate: false,
            });
          }
        } else if (percentageValue === null || percentageValue === undefined) {
          setValue(baseField, undefined, { shouldValidate: false });
        }
      } else if (
        change.name === "propertyTransferTax" ||
        change.name === "notaryAndLandRegistryCosts" ||
        change.name === "realEstateCommission"
      ) {
        const field = change.name;
        const euroValue = values[field] as number;
        const percentageField =
          `${field}Percentage` as keyof PropertyPurchaseCostsForm;
        const currentPercentage = values[percentageField] as number;

        if (
          typeof euroValue === "number" &&
          typeof values.purchasePrice === "number"
        ) {
          const calculatedPercentage = (euroValue / values.purchasePrice) * 100;
          const roundedPercentage =
            Math.round(calculatedPercentage * 100) / 100;

          // Only update if the calculated percentage is different from current value
          if (roundedPercentage !== currentPercentage) {
            setValue(percentageField, roundedPercentage, {
              shouldValidate: false,
            });
          }
        } else if (euroValue === null || euroValue === undefined) {
          setValue(percentageField, undefined, { shouldValidate: false });
        }
      }
    });

    return () => watcher.unsubscribe();
  }, [watch, setValue]);

  useEffect(() => {
    let timeoutId: number;

    watch((values) => {
      timeoutId = window.setTimeout(() => {
        if (values.purchasePrice && values.federalState) {
          handleCalculate(values as PropertyPurchaseCostsForm);
        }
      }, 500);
    });

    const handleCalculate = (data: PropertyPurchaseCostsForm) => {
      const realEstateCommission = data.paysRealEstateCommission
        ? (data.realEstateCommission ?? 0)
        : 0;

      const result: CalculationResult = {
        purchasePrice: data.purchasePrice,
        propertyTransferTax: data.propertyTransferTax ?? 0,
        notaryAndLandRegistryCosts: data.notaryAndLandRegistryCosts ?? 0,
        realEstateCommission,
        totalCosts:
          data.purchasePrice +
          (data.propertyTransferTax ?? 0) +
          (data.notaryAndLandRegistryCosts ?? 0) +
          realEstateCommission,
      };
      setResult(result);
    };

    return () => window.clearTimeout(timeoutId);
  }, [watch, setResult]);

  const chartData = result
    ? {
        labels: [
          t("purchase_price_label"),
          t("property_transfer_tax"),
          t("notary_land_registry_costs"),
          ...(result.realEstateCommission > 0
            ? [t("real_estate_commission")]
            : []),
        ],
        datasets: [
          {
            data: [
              result.purchasePrice,
              result.propertyTransferTax,
              result.notaryAndLandRegistryCosts,
              ...(result.realEstateCommission > 0
                ? [result.realEstateCommission]
                : []),
            ],
            backgroundColor: [
              "#1974DB",
              "#C7375E",
              "#9F81FF",
              ...(result.realEstateCommission > 0 ? ["#F8D082"] : []),
            ],
            borderWidth: 2,
            spacing: 0,
            borderColor: "rgba(244 247 251 / 1)",
          },
        ],
      }
    : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 200,
    },
    plugins: {
      legend: {
        position: "right" as const,
        align: "center" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
        },
        onClick: () => {},
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || "";
            const value = formatMoney(context.parsed);
            return `${label}: ${value}`;
          },
        },
      },
    },
  };

  return (
    <div>
      {data.title && (
        <div className="max-w-[820px] m-auto text-center mb-m">
          <Title className="text-d3 m:text-d2" as="h1">
            {data.title}
          </Title>
        </div>
      )}
      {data.description && (
        <SimpleRichText className="text-body-1 text-neutral-575 mt-3s font-normal max-w-[680px] m-auto text-center mb-m">
          {data.description}
        </SimpleRichText>
      )}
      <div className="grid grid-cols-12">
        <div className="col-span-12 s:col-span-10 s:col-start-2 m:col-span-8 m:col-start-3">
          <div className="grid grid-cols-12 gap-m">
            {/* Form Section */}
            <div className="col-span-12 m:col-span-5 space-y-2s">
              <NumberInput
                name="purchasePrice"
                control={control}
                rules={{
                  required: t("purchase_price_required"),
                  min: { value: 1, message: t("purchase_price_min") },
                }}
                label={t("purchase_price_label")}
                suffix="€"
              />

              <ControlledSelect
                name="federalState"
                control={control}
                label={t("federal_state_label")}
                placeholder={t("federal_state_placeholder")}
                options={federalStateOptions}
                rules={{
                  required: t("federal_state_required"),
                }}
              />

              {purchasePrice && !federalState && (
                <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-md">
                  {t("federal_state_hint_select")}
                </div>
              )}

              {federalState && !purchasePrice && (
                <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-md">
                  {t("federal_state_hint_price")}
                </div>
              )}

              <div>
                <div className="text-title-1 mb-xs">
                  {t("property_transfer_tax")}
                </div>
                <div className="flex items-start gap-2xs">
                  <NumberInput
                    name="propertyTransferTaxPercentage"
                    control={control}
                    rules={{
                      required: t("property_transfer_tax_percentage_required"),
                      min: { value: 0, message: t("percentage_min") },
                    }}
                    suffix="%"
                    className="flex-1"
                    disabled={!costInputsEnabled}
                  />
                  <CalculationIcon icon="EqualSign" className="mt-[14px]" />
                  <NumberInput
                    name="propertyTransferTax"
                    control={control}
                    rules={{
                      required: t("property_transfer_tax_amount_required"),
                      min: { value: 0, message: t("amount_min") },
                    }}
                    suffix="€"
                    className="flex-1"
                    disabled={!costInputsEnabled}
                  />
                </div>
              </div>

              <div>
                <div className="text-title-1 mb-xs">
                  {t("notary_land_registry_costs")}
                </div>
                <div className="flex items-start gap-2xs">
                  <NumberInput
                    name="notaryAndLandRegistryCostsPercentage"
                    control={control}
                    rules={{
                      required: t("notary_land_registry_percentage_required"),
                      min: { value: 0, message: t("percentage_min") },
                    }}
                    suffix="%"
                    className="flex-1"
                    disabled={!costInputsEnabled}
                  />
                  <CalculationIcon icon="EqualSign" className="mt-[14px]" />
                  <NumberInput
                    name="notaryAndLandRegistryCosts"
                    control={control}
                    rules={{
                      required: t("notary_land_registry_amount_required"),
                      min: { value: 0, message: t("amount_min") },
                    }}
                    suffix="€"
                    className="flex-1"
                    disabled={!costInputsEnabled}
                  />
                </div>
              </div>

              <ControlledSwitch
                name="paysRealEstateCommission"
                control={control}
                label={t("real_estate_commission_enabled")}
                labelPosition="end"
              />

              {paysRealEstateCommission && (
                <div>
                  <div className="text-title-1 mb-xs">
                    {t("real_estate_commission")}
                  </div>
                  <div className="flex items-start gap-2xs">
                    <NumberInput
                      name="realEstateCommissionPercentage"
                      control={control}
                      rules={{
                        required: paysRealEstateCommission
                          ? t("real_estate_commission_percentage_required")
                          : false,
                        min: { value: 0, message: t("percentage_min") },
                      }}
                      suffix="%"
                      className="flex-1"
                      disabled={!costInputsEnabled}
                    />
                    <CalculationIcon icon="EqualSign" className="mt-[14px]" />
                    <NumberInput
                      name="realEstateCommission"
                      control={control}
                      rules={{
                        required: paysRealEstateCommission
                          ? t("real_estate_commission_amount_required")
                          : false,
                        min: { value: 0, message: t("amount_min") },
                      }}
                      suffix="€"
                      className="flex-1"
                      disabled={!costInputsEnabled}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Results Section */}
            <div className="col-span-12 m:col-span-7 flex flex-col">
              {result && (
                <>
                  <div className="bg-neutral-12 rounded-m p-m border border-neutral-50">
                    <h3 className="text-h3 mb-s text-align-top">
                      {t("result_title")}
                    </h3>

                    {chartData && (
                      <div className="h-[200px] mb-m">
                        <Doughnut data={chartData} options={chartOptions} />
                      </div>
                    )}

                    <div className="space-y-2xs">
                      <ResultRow
                        label={t("purchase_price_result")}
                        value={result.purchasePrice}
                      />
                      <ResultRow
                        label={t("property_transfer_tax_result")}
                        value={result.propertyTransferTax}
                      />
                      <ResultRow
                        label={t("notary_land_registry_result")}
                        value={result.notaryAndLandRegistryCosts}
                      />
                      {result.realEstateCommission > 0 && (
                        <ResultRow
                          label={t("real_estate_commission_result")}
                          value={result.realEstateCommission}
                        />
                      )}
                      <hr className="border-neutral-100" />
                      <ResultRow
                        label={t("total_costs")}
                        value={result.totalCosts}
                        isTotal
                      />
                    </div>

                    {data.button && (
                      <div className="mt-m">
                        <Button {...data.button} fullWidth />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PropertyPurchaseCostsCalculatorSection = (
  props: PropertyPurchaseCostsCalculatorProps,
) => {
  return (
    <Section section={props.data} className="my-3xl">
      <PropertyPurchaseCostsCalculator {...props} />
    </Section>
  );
};
